import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BarChart3, Check, FileCheck2, ShieldCheck } from "lucide-react";

import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";
import { Button } from "@/components/ui/button";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = `${BASE_URL}/case-studies`;
const CASE_STUDY_URL = `${BASE_URL}/case-studies/local-lead-generation-meta-ads`;
const TITLE = "Meta Ads Case Studies & Verified Results | Adkiro";
const DESCRIPTION =
  "Explore documented Meta Ads case studies from Adkiro, including campaign-level spend, leads, cost per lead, methodology and important performance context.";

export const Route = createFileRoute("/case-studies_")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${BASE_URL}/logo.png?v=20260907` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${PAGE_URL}#page`,
              url: PAGE_URL,
              name: TITLE,
              description: DESCRIPTION,
              isPartOf: { "@id": `${BASE_URL}/#website` },
              mainEntity: {
                "@type": "ItemList",
                numberOfItems: 1,
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "513 Property Leads at a €3.10 Blended CPL",
                    url: CASE_STUDY_URL,
                  },
                ],
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Case Studies", item: PAGE_URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: CaseStudiesPage,
});

function CaseStudiesPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader tone="ink" />

      <main>
        <section data-tone="ink" className="tone-ink relative overflow-hidden">
          <div className="relative mx-auto max-w-5xl px-5 pb-20 pt-16 text-center sm:pt-24">
            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>{" "}
              <span aria-hidden="true">/</span> Case studies
            </nav>
            <p className="mt-8 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium">
              <FileCheck2 className="size-3.5 text-primary" /> Documented first-party evidence
            </p>
            <h1 className="mt-6 text-4xl font-bold leading-[1.08] sm:text-6xl">
              Meta Ads case studies with the numbers and context included
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              We publish the source, calculation method, limitations and campaign-level detail
              behind every result—not only a headline percentage.
            </p>
          </div>
        </section>

        <section aria-labelledby="published-heading" className="mx-auto max-w-6xl px-5 py-16">
          <div className="mb-10 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-primary">Published evidence</p>
            <h2 id="published-heading" className="mt-3 text-3xl font-bold sm:text-4xl">
              Verified campaign results
            </h2>
          </div>
          <article className="glass grid gap-8 rounded-3xl p-7 sm:p-10 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Property lead generation · Sofia
              </p>
              <h3 className="mt-4 text-3xl font-bold">
                513 property leads at a €3.10 blended cost per lead
              </h3>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Seventeen Meta lead campaigns for Valore, our own property business: €1,592.41 in
                recorded spend across 579,821 impressions and every campaign between €1.00 and €4.61
                per lead.
              </p>
              <Button variant="outlineGlow" size="lg" className="mt-7" asChild>
                <Link to="/case-studies/local-lead-generation-meta-ads">
                  Read the campaign breakdown <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["513", "Recorded leads"],
                ["€3.10", "Blended CPL"],
                ["17", "Lead campaigns"],
                ["579,821", "Impressions"],
              ].map(([value, label]) => (
                <div key={label} className="rounded-2xl border border-border bg-background/50 p-5">
                  <p className="font-display text-3xl font-bold text-gradient sm:text-4xl">
                    {value}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-16">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-primary">Evidence standard</p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
              What every Adkiro case study must disclose
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: BarChart3,
                title: "The underlying numbers",
                copy: "Spend, result volume, efficiency metric, timeframe and relevant campaign detail.",
              },
              {
                icon: Check,
                title: "The calculation",
                copy: "How blended figures are produced and which records, if any, are excluded.",
              },
              {
                icon: ShieldCheck,
                title: "The limitations",
                copy: "Account context, result variability and a clear statement that past results are not guarantees.",
              },
            ].map((item) => (
              <article key={item.title} className="glass rounded-3xl p-7">
                <item.icon className="size-6 text-primary" />
                <h3 className="mt-5 text-xl font-semibold">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 pb-24 pt-12">
          <div className="glass relative overflow-hidden rounded-3xl px-6 py-14 text-center">
            <div className="pointer-events-none absolute inset-0 halo" />
            <div className="relative">
              <h2 className="text-3xl font-bold sm:text-4xl">
                See what is happening in your account
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Request a private audit of campaign structure, creative and measurement, then
                compare the recommended scope in our transparent pricing guide.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Button variant="brand" size="xl" asChild>
                  <a href="/#audit">Request a fit review</a>
                </Button>
                <Button variant="outlineGlow" size="xl" asChild>
                  <Link to="/meta-ads-pricing">
                    Compare pricing <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
